"use client";

import { Activity, Dna, HeartPulse, Stethoscope, Briefcase, Radio, ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Services() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: gridRef,
    isVisible: gridVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: ctaRef,
    isVisible: ctaVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.3
  });
  const services = [{
    number: "01",
    title: "OTA Revenue Management",
    description: "Optimize your presence on Booking.com, Expedia, and Agoda with data-driven pricing and inventory strategies.",
    Icon: Stethoscope
  }, {
    number: "02",
    title: "Hospitality Tech Implementation",
    description: "Seamless integration of PMS, Channel Managers, and Booking Engines to streamline your hotel operations.",
    Icon: HeartPulse
  }, {
    number: "03",
    title: "Revenue Audits",
    description: "Comprehensive analysis of your current revenue streams to identify hidden growth opportunities and inefficiencies.",
    Icon: Dna
  }, {
    number: "04",
    title: "Dynamic Pricing Strategy",
    description: "Real-time rate adjustments based on market demand, competitor analysis, and historical booking patterns.",
    Icon: Activity
  }, {
    number: "05",
    title: "Direct Booking Optimization",
    description: "Strategies to shift bookings from high-commission OTAs to your own website, increasing your net profit.",
    Icon: Briefcase
  }, {
    number: "06",
    title: "Market Intelligence Reporting",
    description: "Actionable insights and performance reports to keep you ahead of local market trends and competition.",
    Icon: Radio
  }];
  return <section data-section-id="5097" id="indianmarketservices" className="bg-slate-50 text-slate-900 py-20 md:py-28 lg:py-32">
      <div className="container mx-auto px-6 lg:px-10 max-w-7xl">
        
        <div ref={headerRef} className={`grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 mb-16 md:mb-20 lg:mb-24 transition-all duration-1000 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="lg:col-span-7">
            <div className="flex items-center gap-3 mb-8">
              <span className="font-mono text-xs tracking-widest uppercase text-blue-600">
                01 — Our Expertise
              </span>
              <span className="h-px w-12 bg-blue-600/40" />
            </div>
            <h2 className="font-default font-bold text-5xl md:text-6xl lg:text-7xl xl:text-[80px] leading-[1.05] tracking-tight text-slate-950">
              Strategic revenue
              <br />
              <span className="italic font-light text-blue-900">growth solutions.</span>
            </h2>
          </div>
          <div className="lg:col-span-5 lg:pt-6 flex lg:justify-end">
            <p className="font-default text-base md:text-lg leading-relaxed text-slate-700 max-w-[480px] lg:text-right">
              At HotelRevBaBa, we combine deep industry expertise with advanced data analytics to maximize your property's profitability. Explore our core pillars of revenue management and digital strategy.
            </p>
          </div>
        </div>

        
        <div ref={gridRef} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((service, i) => {
          const Icon = service.Icon;
          return <div data-index={i} key={i} className={`group relative bg-white text-slate-900 border border-slate-200 p-8 rounded-lg overflow-hidden transition-all duration-700 ease-out hover:-translate-y-2 hover:shadow-lg ${gridVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`} style={{
            transitionDelay: gridVisible ? `${i * 100}ms` : "0ms"
          }}>
                
                <span className="absolute top-0 left-0 h-[2px] w-0 bg-blue-600 transition-all duration-500 ease-out group-hover:w-full" />

                
                <div className="relative inline-flex items-center justify-center mb-8">
                  <Icon className="size-10 text-blue-900 transition-transform duration-500 group-hover:scale-110" strokeWidth={1.25} />
                  <span className="absolute -top-1 -right-2 flex h-2 w-2">
                    <span className="absolute inline-flex h-full w-full rounded-lg bg-blue-600 opacity-75 animate-ping" />
                    <span className="relative inline-flex rounded-lg h-2 w-2 bg-blue-600" />
                  </span>
                </div>

                
                <div className="font-default italic text-blue-600 text-base mb-3">
                  {service.number}
                </div>

                
                <h3 className="font-default text-[22px] font-semibold leading-snug mb-4 tracking-tight text-slate-950">
                  {service.title}
                </h3>

                
                <p className="font-default text-sm leading-relaxed text-slate-600 line-clamp-3 mb-8">
                  {service.description}
                </p>

                
                <Link to="#" className="inline-flex items-center gap-2 text-blue-700 text-sm font-semibold font-default group/link">
                  <span>Learn more</span>
                  <ArrowRight className="size-4 transition-transform duration-300 group-hover/link:translate-x-1" />
                </Link>
              </div>;
        })}
        </div>

        
        <div ref={ctaRef} className={`mt-16 md:mt-20 flex justify-center transition-all duration-1000 ease-out ${ctaVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <Button asChild variant="link" className="text-slate-900 hover:text-blue-700 text-base font-default font-semibold group/cta">
            <Link to="#" className="inline-flex items-center gap-2">
              <span className="border-b border-slate-900/30 group-hover/cta:border-blue-700 pb-1 transition-colors">
                View all revenue services
              </span>
              <ArrowRight className="size-4 transition-transform duration-300 group-hover/cta:translate-x-1" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}